"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { IconArrowUpRight, IconStarFilled, IconWorld } from "@tabler/icons-react";
import {
  featuredProjectId,
  projectGroups,
  projectImages,
  projects,
  splitTitle,
  type ProjectGroup,
} from "@/constants/projects";
import { engagements } from "@/constants/site";
import ProjectCard from "@/components/projectCard";
import { CtaSection } from "@/components/home/ProcessAndCta";

type Filter = "All" | ProjectGroup;

const featured = projects.find((p) => p.id === featuredProjectId)!;
const featuredHighlights = engagements.find((e) => e.projectId === featuredProjectId)?.highlights ?? [];

export default function ProjectsClient() {
  const [filter, setFilter] = React.useState<Filter>("All");

  const visible = projects.filter(
    (p) => p.id !== featuredProjectId && (filter === "All" || p.group === filter)
  );
  const showFeatured = filter === "All" || featured.group === filter;

  const { name: featuredName, kind: featuredKind } = splitTitle(featured.title);
  const featuredCover = projectImages(featured)[0];
  const featuredHref = `/projects/${featured.slug}`;

  return (
    <div className="mx-auto flex max-w-[1150px] flex-col gap-5 pt-6 xl:max-w-[1440px]">
      {/* Header */}
      <section className="relative overflow-hidden rounded-[20px] border border-white/[0.06] bg-black p-5 sm:p-6 md:p-10">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#48AEEF]/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-[#F7B500]/10 blur-3xl"
        />
        <div className="relative">
          <div>
            <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#48AEEF]">
              Portfolio
            </span>
            <h1 className="mt-3 max-w-[720px] text-[32px] font-bold leading-[1.1] tracking-tight md:text-[48px]">
              Projects that{" "}
              <span className="bg-gradient-to-r from-[#48AEEF] via-[#7CC8F5] to-[#F7B500] bg-clip-text text-transparent">
                run real businesses.
              </span>
            </h1>
            <p className="mt-4 max-w-[620px] text-[16px] leading-relaxed text-[#868E96] md:text-[18px]">
              Configurators, management systems, trading dashboards and AI products, built end to end
              for clients in energy, solar, fintech and SaaS.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 py-1" role="tablist" aria-label="Filter projects">
        {(["All", ...projectGroups] as Filter[]).map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(f)}
              className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-[14px] font-semibold transition-colors ${
                active
                  ? "border-white bg-white text-black"
                  : "border-white/[0.08] bg-black text-[#A5B0BC] hover:border-white/20 hover:text-white"
              }`}
            >
              {f}
            </button>
          );
        })}
      </div>

      {/* Featured */}
      {showFeatured && (
        // Whole card is clickable through the title link's ::after overlay; the live-demo link sits above it
        <article
          className="group relative grid cursor-pointer overflow-hidden rounded-[20px] border border-[#F7B500]/20 bg-gradient-to-br from-[#1a1508] via-black to-black transition-colors hover:border-[#F7B500]/40 lg:grid-cols-[1.15fr_1fr]"
        >
          <div className="relative m-3 overflow-hidden rounded-[14px] bg-[#0E1018] lg:mr-0">
            <Image
              src={featuredCover.src}
              alt={featuredCover.alt}
              width={1600}
              height={1000}
              sizes="(min-width: 1024px) 55vw, 95vw"
              className="h-[240px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] sm:h-[340px] lg:h-full lg:min-h-[420px]"
              priority
            />
          </div>

          <div className="flex flex-col p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F7B500]/15 px-2.5 py-1 text-[12px] font-bold text-[#F7B500]">
                <IconStarFilled size={12} />
                Featured
              </span>
              {featured.category && (
                <span className="rounded-full bg-[#48AEEF]/10 px-2.5 py-1 text-[12px] font-semibold text-[#48AEEF]">
                  {featured.category}
                </span>
              )}
            </div>

            <h2 className="mt-4 text-[28px] font-bold leading-tight md:text-[36px]">
              <Link href={featuredHref} className="after:absolute after:inset-0 after:content-['']">
                {featuredName}
              </Link>
            </h2>
            {featuredKind && <p className="mt-1 text-[16px] font-medium text-[#A5B0BC]">{featuredKind}</p>}

            <ul className="mt-5 space-y-2.5">
              {featuredHighlights.map((h) => (
                <li key={h} className="flex gap-2.5 text-[15px] leading-relaxed text-[#9AA3AD]">
                  <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F7B500]" />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-5 flex flex-wrap gap-1.5">
              {(featured.technologies ?? []).map((t) => (
                <span
                  key={t}
                  className="rounded-[7px] border border-white/[0.06] bg-[#0E1018] px-2 py-1 text-[12px] font-medium text-[#A5B0BC]"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-auto flex flex-col gap-3 pt-7 sm:flex-row">
              <span aria-hidden className="inline-flex h-11 items-center justify-center gap-2 rounded-[12px] bg-white px-5 text-[15px] font-bold text-black">
                Read case study
                <IconArrowUpRight size={17} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
              {featured.demoUrl && (
                <Link
                  href={featured.demoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="relative z-10 inline-flex h-11 items-center justify-center gap-2 rounded-[12px] border border-white/10 bg-[#0E1018] px-5 text-[15px] font-bold text-white transition-colors hover:bg-[#161a26]"
                >
                  <IconWorld size={17} />
                  Live demo
                </Link>
              )}
            </div>
          </div>
        </article>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {visible.map((project) => (
          <ProjectCard key={project.id} project={project} href={`/projects/${project.slug}`} />
        ))}
      </div>

      <CtaSection />
    </div>
  );
}
